import { useAuthContext } from '@/hooks/useAuthContext';
import { TRPCProvider } from '@/utils/trpc';
import type { PatientAdminApiRouter } from '@liza/patient-admin-api';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import type { PropsWithChildren } from 'react';
import { useState } from 'react';
import superjson from 'superjson';

export const AuthenticatedRootComponent = ({ children }: PropsWithChildren): React.ReactNode => {
  const { oktaAuth } = useAuthContext();
  const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false } } });
  const [trpcClient] = useState(() =>
    createTRPCClient<PatientAdminApiRouter>({
      links: [
        httpBatchLink({
          url: `${import.meta.env.VITE_PATIENT_ADMIN_API_URL}/trpc`,
          transformer: superjson,
          // API Gateway rejects requests whose combined request line + headers exceed ~10KB
          // before the Lambda runs, so the response carries no CORS headers and the browser
          // reports a misleading CORS failure. Split oversized batches into multiple requests
          // well under that limit (leaving headroom for the Authorization header).
          maxURLLength: 8000,
          headers: async () => {
            const token = await oktaAuth?.getOrRenewAccessToken();
            return token ? { Authorization: `Bearer ${token}` } : {};
          },
        }),
      ],
    }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
};
